(ns minorodata.components.connections.sources-and-extracts
  (:require
   [clojure.set :as c.set]
   [clojure.string :as s]
   [lucide-react :refer [AlertTriangle ArrowDownUp BookOpen ChevronDown
                         MoreHorizontal PlayCircle Plus RefreshCw Utensils
                         Search Menu Info]]
   [applied-science.js-interop :as js-interop]
   [minorodata.app.handlers :refer [dispatch dispatch-sync]]
   [minorodata.app.utils :as app.utils]
   [minorodata.components.connections.extract-details-drawer :refer [extract-details-drawer job-status-mapping]]
   [minorodata.components.connections.recipes :refer [deploy-recipes-dialog]]
   [minorodata.components.infinite-scroll.core :as c.infinite-scroll]
   [minorodata.components.schedule.core :as c.schedule]
   [minorodata.components.spinner.core :as c.spinner]
   [minorodata.components.tables.details :as c.t-details]
   [minorodata.components.transformations.groups :refer [spinner-list-item]]
   [minorodata.utils :as utils]
   [mold-forms.core :as form-component]
   [mold-forms.model :refer [->form-model]]
   [re-posh.core :as re-posh]
   [react :as react]
   [reagent.core :as reagent]
   [reitit.frontend.easy :as rt.easy]
   [ui.button :refer [button]]
   [ui.chip :as chip]
   [ui.drawer :refer [drawer-root drawer-content-resizable]]
   [ui.date-time :refer [date-tooltip]]
   [ui.dialog :refer [dialog-actions dialog-content dialog-root dialog-title]]
   [ui.dropdown-menu :refer [dropdown-menu-content dropdown-menu-item
                             dropdown-menu-root dropdown-menu-trigger]]
   [ui.fields :as fields]
   [ui.page :refer [page-columns]]
   [ui.table :refer [sort-label table tbody td th thead tr]]
   [ui.tooltip :refer [tooltip]]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]
   ["allotment" :refer [Allotment]]
   ["virtua" :refer [Virtualizer]]))

(defn show-source-edit-modal [{:keys [type id]}]
  (dispatch [:connector/add {:connector/action :edit-source
                             :connector/type   type
                             :connector/id     id}]))

(defn show-source-delete-modal [{:keys [type id]}]
  (dispatch [:connector/add {:connector/action :delete-source
                             :connector/type   type
                             :connector/id     id}]))

(defn show-create-extract-modal [{:keys [type id]}]
  (dispatch [:connector/add {:connector/action :create-extract
                             :connector/type   type
                             :connector/id     id}]))


(defn open-table-info-modal [config]
  (let [[table schema catalog]
        (some-> (get-in config [:extracts 0 :destination-table])
                (clojure.string/split #"\.")
                (reverse))]
    (c.t-details/open-table-info-modal {:db catalog :schema schema :table table})))


(defn job-status-display [status]
  (let [status-config (case status
                        (:failed :fail) {:color "bg-kleene-text-error" :title "Failed"}
                        :success {:color "bg-kleene-text-success" :title "Finished"}
                        :running {:color "bg-kleene-text-secondary" :title "Running"}
                        :starting {:color "bg-kleene-text/50" :title "Starting"}
                        :pending {:color "bg-kleene-text/50" :title "Pending"}
                        {:color "bg-kleene-text/50" :title status})]
    (when (some? status)
      [:div {:class "inline-block align-middle"}
       [:div {:title   (:title status-config)
              :data-cy (str "job-status-"
                            (if (some? status)
                              (name status)
                              "unknown"))
              :class (clsx "h-3 w-3 rounded-full" (:color status-config))}]])))

(defn mq-extract-status-display [extract-id]
  (let [status     @(re-posh/subscribe [:extract-task-status/prop [:extract-task-status/extract-id extract-id] :extract-task-status/status])
        job-status (get job-status-mapping status)]
    [job-status-display job-status]))

(defn mq-extract-date-display [extract-id client-timezone]
  (let [date @(re-posh/subscribe [:extract-task-status/prop [:extract-task-status/extract-id extract-id] :extract-task-status/created-at])]
    [date-tooltip {:date date :timezone client-timezone}]))

(defn show-extract-drawer
  [extract-id]
  (let [extract @(re-posh/subscribe [:extract/pull [:extract/extract-id extract-id]])
        source  @(re-posh/subscribe [:source/pull [:source/source-id (:extract/source-id extract)]])]
    (dispatch [:connector/add {:connector/action :edit-extract
                               :connector/type   (:source/source-type source)
                               :connector/id     extract-id}])))

(defn fetch-extract-for-drawer
  [extract-id source-id]
  (dispatch [:connections.api/fetch-extract
             {:extract-id extract-id
              :source-id  source-id}]))

(defn sources-list-component [props & children]
  (into [:div (update props :class #(clsx "flex flex-col overflow-y-auto" %))]
        children))

(defn source-list-item [{:keys [source active-id connectors]}]
  (let [{:source/keys [source-id source-type source name updated-by updated-at]} source
        failures  @(re-posh/subscribe [:connections.subs/failed-statuses-count-for-source source-id])
        version   (:version source)
        connector (app.utils/find-connector-by-type-and-version connectors source-type version)
        selected  (= active-id source-id)

        connector-latest-version (:version (app.utils/find-connector-by-type-and-version connectors source-type nil))]

    [:a {:title    name
         :data-cy (str "sources-list-item sources-item-" source-id)
         :href    (rt.easy/href :source-details {:source-id source-id})
         :class   (clsx "px-5 py-3 hover:bg-kleene-hover-background transition-colors border-b group cursor-pointer flex justify-between gap-2 items-center"
                        {"bg-kleene-selected-background" selected})
         :data-selected (when selected "true")}
     [:div {:class "shrink overflow-hidden w-full"}
      [typography/h3 {:class "flex items-center justify-between gap-2 overflow-hidden"}
       [:span {:class "shrink truncate"} name]
       (when (:is-exporter connector)
         [chip/chip {:variant :secondary}
          "Exporter"])
       (when-not (= version connector-latest-version)
         [tooltip {:content "This source is not using the latest connector version"
                   :class "text-center"}
          [:> Info {:class "text-kleene-warning-02" :size 20}]])]

      [:div {:class "flex flex-row gap-2 mt-1"}
       [:div {:class "text-xs grow"}
        [:div
         (when @(re-posh/subscribe [:unleash/is-enabled "fe.sidebar-failures-count"])
           (if (pos? failures)
             [:span {:class "text-kleene-error-01"}
              (str failures " failed")]
             [:span {:class "text-kleene-text-secondary"}
              "No failures"]))]
        (when (and updated-at updated-by)
          [typography/small {:class "text-kleene-text-secondary"}
           "Updated " [date-tooltip {:date updated-at}] [:br {:class "2xl:hidden"}] " by " (:searchable-user/username updated-by)])]

       (when (:image-source connector)
         [:div {:class ["bg-white rounded py-2 px-0.5 overflow-hidden flex items-center shrink-0"
                        "@[300px]/sources:hidden"]}
          [:img {:loading "lazy"
                 :title   source-type
                 :src     (app.utils/maybe-local-url (:image-source connector))
                 :width   60
                 :height  18
                 :class   "object-contain bg-none border-none h-[18px]"}]])]]

     (when (:image-source connector)
       [:div {:class ["bg-white rounded-xs py-2 px-0.5 overflow-hidden items-center shrink-0 hidden"
                      "@[300px]/sources:block"]}
        [:img {:loading "lazy"
               :title   source-type
               :src     (app.utils/maybe-local-url (:image-source connector))
               :width   60
               :height  18
               :class   "object-contain bg-none border-none h-[18px]"}]])]))

(defn empty-message [{:keys [switch-tab]}]
  [:div {:class "flex items-center justify-center w-full h-full"}
   [:div
    [typography/h2
     "You don't have any sources set up yet."]
    [typography/p "Please connect to a source first. After you have set up a valid source, you will be able to manage data extracts here."]
    [button {:class    "mt-4"
             :on-click #(switch-tab "sources")
             :data-cy  "connections-connect-source-btn"}
     "Connect new Source"]]])

(def sort-options
  [{:id    :default
    :title "By source type"}
   {:id    :name
    :title "By name"}
   {:id    :name-desc
    :title "By name (DESC)"}
   {:id    :last-updated
    :title "By last updated"}
   {:id    :last-updated-desc
    :title "By last updated (DESC)"}
   {:id    :update-person
    :title "By person"}
   {:id    :update-person-desc
    :title "By person (DESC)"}])

(def sort-items-mapping
  {[nil nil]           :default
   [:name :ASC]        :name
   [:name :DESC]       :name-desc
   [:updated_at :ASC]  :last-updated
   [:updated_at :DESC] :last-updated-desc
   [:updated_by :ASC]  :update-person
   [:updated_by :DESC] :update-person-desc})

(def sort-items-mapping-invert
  (c.set/map-invert sort-items-mapping))

(defn select-sort-option [option-id]
  (let [[order-by order-dir] (get sort-items-mapping-invert option-id)]
    (dispatch [:app.events/set-local-storage "sources-panel-sort" {:order-by order-by
                                                                   :order-dir order-dir}])
    (dispatch [:connections.api/fetch-sources {:pagination {:page 1}
                                               :sort (when order-by
                                                       {:order-by  order-by
                                                        :order-dir order-dir})
                                               :replace?  true}])))

(defn filtered-sources' [sources search]
  (if (clojure.string/blank? search)
    sources
    (let [search-pattern (re-pattern (str "(?i).*" search ".*"))]
      (filter (fn [source]
                (re-matches search-pattern (or (:source/name source) "")))
              sources))))

(def filtered-sources (memoize filtered-sources'))

(defn filtered-deleted-sources' [sources search]
  (if (clojure.string/blank? search)
    sources
    (let [search-pattern (re-pattern (str "(?i).*" search ".*"))]
      (filter (fn [source]
                (or
                 (re-matches search-pattern (get source :deleted-source/name ""))
                 (re-matches search-pattern (get source :deleted-source/description ""))))
              sources))))

(def filtered-deleted-sources (memoize filtered-deleted-sources'))

(defn restore-sources-dialog []
  (let [route @(re-posh/subscribe [:app-route/pull])
        {:app-route/keys [query]} route
        restore-sources? (:restore-deleted-sources query)

        {:keys [deleted-sources deleted-sources-loading?]} @(re-posh/subscribe [:connections.subs/deleted-sources-list-view])

        [search update-search] (react/useState "")
        [selected update-selected] (react/useState #{})

        on-close      (fn []
                        (update-selected #{})
                        (dispatch [:app.events/navigate-query {:restore-deleted-sources nil :restore-deleted-extracts nil}]))
        on-success    (fn []
                        (doall (map (fn [source-id]
                                      (dispatch [:connections.api/restore-deleted-source {:source-id source-id}]))
                                    selected))
                        (on-close)
                        (js/setTimeout
                         (fn []
                           (dispatch [:app.events/navigate :source-details {:source-id (first selected)}])
                           (dispatch [:connections.events/load-data]))
                         1000))]

    (react/useEffect
     (fn []

       (when restore-sources?
         (dispatch [:connections.api/fetch-deleted-sources]))

       ;; return unsubscribe callback
       (fn []))
     #js [restore-sources?])


    [dialog-root {:open           restore-sources?
                  :on-open-change (fn [val]
                                    (when-not val (on-close)))}
     [dialog-content {:x-close true}
      [dialog-title "Archived sources"]
      [:div {:class "mt-4 max-h-[60vh] overflow-hidden flex flex-col"}


       (if deleted-sources-loading?
         [:div {:class "p-4 flex justify-center"}
          [c.spinner/spinner-lucide]]


         [:div {:class "h-full overflow-auto"}
          [fields/input {:class         "min-w-0 mr-2 mb-5"
                         :placeholder   "Search by name"
                         :default-value search
                         :type          "search"
                         :on-change     (fn [e]
                                          (update-search (js-interop/get-in e [:target :value])))
                         :name          "search-entity"
                         :input-props   {:auto-complete "off"}
                         :icon          Search}]


          [table
           [thead
            [tr {:sticky true
                 :class  "*:top-0 bg-white dark:bg-kleene-dark-02"}
             [th {:class "p-0"}]
             [th {:class "w-full"}
              "Source name"]]]

           [tbody
            (for [{:deleted-source/keys [name source-id description]} (filtered-deleted-sources deleted-sources search)]
              (let [selected? (selected source-id)]
                [tr {:class "group"}
                 [td {:class "p-0"}
                  [fields/checkbox {:checked   selected?
                                    :class     ""
                                    :on-change (fn [] (update-selected
                                                       (if selected?
                                                         (disj selected source-id)
                                                         (conj selected source-id))))}]]
                 [td {:class "max-w-[250px] overflow-hidden"}
                  [:div {:title name :class "text-ellipsis overflow-hidden whitespace-nowrap"} name]
                  (when description
                    [typography/p {:class "text-sm break-all text-kleene-text-secondary"}
                     description])]]))]]])]
      [dialog-actions
       [ui.button/button {:data-cy  "source-restore-btn"
                          :disabled (= (count selected) 0)
                          :on-click on-success}

        (if (> (count selected) 0)
          (str "Restore " (count selected) " selected " (if (= 1 (count selected)) "source" "sources"))
          "Restore selected sources")]]]]))


(defn restore-extracts-dialog []
  (let [route @(re-posh/subscribe [:app-route/pull])
        {:app-route/keys [query]} route
        source-id (:restore-deleted-extracts query)
        restore-extracts? (not (nil? source-id))

        {:keys [extracts extracts-loading?]} @(re-posh/subscribe [:connections.subs/deleted-extracts-list-view])

        on-close      #(dispatch [:app.events/navigate-query {:restore-deleted-sources nil :restore-deleted-extracts nil}])
        on-success    (fn [source-id extract-id]
                        (on-close)
                        (dispatch [:connections.api/restore-deleted-extract source-id extract-id]))]

    (react/useEffect
     (fn []

       (when source-id
         (dispatch [:connections.api/fetch-deleted-extracts source-id]))

       ;; return unsubscribe callback
       (fn []))
     #js [source-id])

    [dialog-root {:open           restore-extracts?
                  :on-open-change (fn [val]
                                    (when-not val (on-close)))}
     [dialog-content {:x-close true}
      [dialog-title "Restore previously deleted extracts"]
      [:div {:class "mt-4"}
       [:div {:class "mt-4"}
        [table
         [thead
          [tr {:sticky true
               :class  "*:top-0 bg-white dark:bg-kleene-dark-02"}
           [th {:class "p-0"}]
           [th {:class "w-full"}
            "Extract name"]
           [th {:class "w-full"}
            "Deleted at"]]]

         [tbody
          (for [{:deleted-extract/keys [name extract-id description]} extracts]
            (let [selected false]
              [tr {:class "group"}
               [td {:class "p-0"}
                [fields/checkbox {:checked   selected
                                  :class     (clsx "opacity-0 group-hover:opacity-100 transition-opacity"
                                                   {"opacity-100" selected})
                                  :on-change (fn [])}]]
               [td {:class "max-w-[250px] overflow-hidden"}
                [:div {:title name :class "text-ellipsis overflow-hidden whitespace-nowrap"} name]
                (when description
                  [typography/p {:class "text-sm break-all text-kleene-text-secondary"}
                   description])]]))]
         [dialog-actions
          [ui.button/button {:data-cy  "source-restore-btn"
                             :on-click on-success}
           "Restore selected extracts"]]]]]]]))


(defn sidebar [{:keys [sources connectors active-id loading? order-by order-dir]}]
  (let [ref (react/useRef nil)
        [search update-search] (react/useState "")
        selected (get sort-items-mapping [order-by order-dir])]

    (react/useEffect
     (fn []
       (when-not loading?
         (js/setTimeout
          #(some-> (.-current ref)
                   (.querySelector "[data-selected]")
                   app.utils/scroll-into-view)
          100))
       js/undefined)
     #js [loading?])

    [:div {:class "flex flex-col overflow-hidden"}

     (when @(re-posh/subscribe [:unleash/is-enabled "fe.extracts-restore"])
       [:f> restore-sources-dialog]
     ; [:f> restore-extracts-dialog]
       )

     [:header {:class "shrink-0 border-b px-5 py-4 flex flex-col bg-kleene-background gap-2"}
      [:div {:class "flex justify-between items-center bg-kleene-background"}
       [typography/h2 "Sources and extracts"]

       (when @(re-posh/subscribe [:unleash/is-enabled "fe.extracts-restore"])
         [dropdown-menu-root
          [dropdown-menu-trigger
           [tooltip {:content "Actions..."}
            [button {:size      :icon-md
                     :variant   :secondary
                     :component :div
                     :data-cy   "sources-menu-btn"
                     :icon      MoreHorizontal}]]]
          [dropdown-menu-content
           [dropdown-menu-item {:on-click (fn []
                                            (dispatch [:app.events/navigate-query {:restore-deleted-sources true :restore-deleted-extracts nil}]))
                                :data-cy  "restore-sources-btn"}
            "View archive"]]])]

      [:div {:class "flex flex-row gap-2"}
       [fields/input {:class         "min-w-0"
                      :placeholder   "Search by name"
                      :default-value search
                      :type          "search"
                      :on-change     (fn [e]
                                       (update-search (js-interop/get-in e [:target :value])))
                      :name          "search-entity"
                      :input-props   {:auto-complete "off"}
                      :icon          Search}]
       [dropdown-menu-root
        [dropdown-menu-trigger
         [tooltip {:content "Sort sources"}
          [button {:size      :icon-md
                   :variant   :secondary
                   :component :div
                   :data-cy   "groups-sort-btn"
                   :icon      ArrowDownUp}]]]
        (into [dropdown-menu-content]
              (for [{:keys [id title]} sort-options]
                [dropdown-menu-item {:on-click #(select-sort-option id)
                                     :data-cy  (str "group-sort-option-" id "-btn")
                                     :active   (= id selected)}
                 title]))]]]

     [:div {:class "flex-1 overflow-hidden @container/sources"}
      (when-not (and (= (count sources) 0)
                     (not loading?))
        (into
         [c.infinite-scroll/main
          {:component       sources-list-component
           :loader          [spinner-list-item]
           :loading?        loading?
           :on-scroll-end   #()
           :node-ref        ref}]
         (for [source (filtered-sources sources search)]
           ^{:key (:id source)}
           [source-list-item {:source     source
                              :connectors connectors
                              :active-id  active-id}])))]]))

(defn source-menu [{:keys [source]}]
  (let [{:source/keys [source-type source-id]} source]
    [dropdown-menu-root
     [dropdown-menu-trigger {:asChild false}
      [button {:size      :icon
               :variant   :secondary
               :icon      MoreHorizontal
               :data-cy   "source-more-btn"
               :component :div}]]
     [dropdown-menu-content
      [dropdown-menu-item
       {:on-click #(show-source-edit-modal {:id source-id :type source-type})
        :data-cy "source-edit-btn"}
       "Edit source"]
      #_[dropdown-menu-item
         {:on-click (fn []
                      (dispatch [:app.events/navigate-query {:restore-deleted-sources nil :restore-deleted-extracts source-id}]))
          :data-cy "source-restore-extracts-btn"}
         "Restore deleted extracts"]
      [dropdown-menu-item
       {:on-click #(show-source-delete-modal {:id source-id :type source-type})
        :class    "text-kleene-text-error"
        :data-cy  "source-delete-btn"}
       "Archive source"]]]))

(defn extracts-table-wrapper [props & children]
  (into [:div (update props :class #(clsx "px-5 overflow-auto! pb-16" %))]
        children))

(defn row-actions [{:keys [source extract source-definition extract-details-drawer?]}]
  (let [{:extract/keys [extract-id name config]} extract
        {:source/keys [source-id]} source

        notifications-enabled? @(re-posh/subscribe [:unleash/is-enabled "fe.notifications"])
        show-table-details?    @(re-posh/subscribe [:app.subs/enabled-for-bigquery :tables])]
    [:div {:class "flex gap-2 justify-end items-center"}
     [tooltip {:trigger-as-child false
               :content          "Run extract"}
      [:div {:on-click  #(dispatch [:connections.api/start-extract-by-id
                                    {:extract-id extract-id
                                     :name       name
                                     :source-id  source-id
                                     :source     source-definition}])
             :data-cy   "extract-start-btn"}
       [:> PlayCircle {:class (clsx "block cursor-pointer hover:text-kleene-secondary-v2 transition-colors")}]]]

     (when-not extract-details-drawer?
       [dropdown-menu-root
        [dropdown-menu-trigger {:data-cy "extract-more-btn"}
         [:> MoreHorizontal {:class (clsx "block cursor-pointer hover:text-kleene-secondary-v2 transition-colors")}]]
        [dropdown-menu-content
         [dropdown-menu-item {:data-cy  "extract-edit-btn"
                              :on-click #(show-extract-drawer extract-id)}
          "Edit"]

         [dropdown-menu-item {:on-click #(dispatch [:connector/add
                                                    {:connector/action :schedule-extract
                                                     :connector/type   (:source/source-type source)
                                                     :connector/id     extract-id}])
                              :data-cy  "extract-schedule-btn"}
          "Schedule"]

         [dropdown-menu-item
          [:a {:class "block w-full"
               :data-cy "extract-view-logs-btn"
               :href (rt.easy/href :log {} {:source (:source/source-type source)
                                            :term   name})}
           "View logs"]]

         (when notifications-enabled?
           [dropdown-menu-item
            {:data-cy  "extract-notifications-btn"
             :on-click #(dispatch [:connector/add
                                   {:connector/action :set-notification
                                    :connector/type   (:source/source-type source)
                                    :connector/id     extract-id}])}
            "Notifications"])

         (when show-table-details?
           [dropdown-menu-item
            [:a {:on-click #(open-table-info-modal config)
                 :data-cy  "extract-destination-table-info-btn"
                 :class    "block w-full"}
             "Destination table info"]])

         [dropdown-menu-item {:data-cy "extract-clone-btn"
                              :on-click (fn []
                                          (dispatch [:connector/add {:connector/action :clone-extract
                                                                     :connector/type   (:source/source-type source)
                                                                     :connector/id     source-id
                                                                     :connector/meta   {:extract extract}}]))}
          "Clone extract"]

         [dropdown-menu-item {:on-click #(dispatch [:connector/add
                                                    {:connector/action :delete-extract
                                                     :connector/type   (:source/source-type source)
                                                     :connector/id     extract-id}])
                              :data-cy  "extract-delete-btn"
                              :class    "text-kleene-error-01"}
          "Delete"]]])]))


(defn sort-extracts [source-id order-field clear-selection]
  (let [{:extract-panel/keys [sort]}
        @(re-posh/subscribe [:extract-panel/pull])
        {:keys [order-by order-dir]} sort
        active? (= order-field order-by)
        next-order-dir (if active?
                         (if (= (keyword order-dir) :ASC) :DESC :ASC)
                         :ASC)]
    (dispatch [:app.events/set-local-storage "extracts-panel-sort"
               {:order-by  order-field
                :order-dir next-order-dir}])
    ;; Sorting replaces the loaded list, so a stale selection could outnumber it.
    (clear-selection)
    (dispatch [:connections.api/fetch-extracts
               {:source-id  source-id
                :pagination {:page 1 :items-per-page 50}
                :sort       {:order-by  order-field
                             :order-dir next-order-dir}
                :replace    true}])))

(defn extracts-table-head [{:keys [source-id source-details selected on-toggle on-clear-selection]}]
  (let [{:extract-panel/keys [sort]}
        @(re-posh/subscribe [:extract-panel/pull])
        {:keys [order-by order-dir]}    sort
        {:keys [message-queue-enabled]} source-details

        timezone  @(re-posh/subscribe [:app.subs/timezone])]
    [thead
     [tr {:sticky true
          :class  "*:top-0 bg-white dark:bg-kleene-dark-02"}

      [th {:class "w-14 pl-3 pr-2"}
       [fields/checkbox {:checked   selected
                         :on-change on-toggle}]]
      [th {:class "w-full"}
       [sort-label {:active    (= order-by "name")
                    :direction order-dir
                    :on-click  #(sort-extracts source-id "name" on-clear-selection)}
        "Extract name"]]

      [th {:class "min-w-[200px] 2xl:min-w-[300px]"}
       [sort-label {:active    (= order-by "dow, hour, minute")
                    :direction order-dir
                    :on-click  #(sort-extracts source-id "dow, hour, minute" on-clear-selection)}
        [:div
         "Schedule"
         [:div {:class "-mt-1"}
          [:span {:class "text-xs text-kleene-dark-05 dark:text-kleene-dark-03"} timezone]]]]]

      [th {:class "min-w-[200px] 2xl:min-w-[300px]"}
       [:div {:class "flex gap-2 items-center"}
        (when message-queue-enabled
          [:> RefreshCw {:size     20
                         :title    "Update status"
                         :data-cy  "update-extract-status-btn"
                         :class    "cursor-pointer absolute right-10 transition-colors text-kleene-text-secondary hover:text-kleene-text"
                         :on-click #(dispatch [:connections.api/fetch-extract-statuses-for-source source-id
                                               {:on-success (fn []
                                                              (dispatch [:app.events/add-snack :success "Statuses updated"]))}])}])
        [sort-label {:class     "flex-1"
                     :active    (= order-by "last_run_at")
                     :direction order-dir
                     :on-click  #(sort-extracts source-id "last_run_at" on-clear-selection)}
         [:div {:class "_flex items-center gap-2"}

          "Last run"
          [:div {:class "-mt-1"}
           [:span {:class "text-xs text-kleene-dark-05 dark:text-kleene-dark-03"} timezone]]]]]]

      [th {:class "text-right min-w-[60px] w-full"}]]]))

(defn extracts-table-row [{:keys [extract jobs source source-definition selected active? on-toggle on-open extract-details-drawer?]}]
  (let [{:extract/keys [extract-id name description status last-run-at last-run-status]} extract
        {:keys [message-queue-enabled]} source-definition

        timezone   @(re-posh/subscribe [:app.subs/timezone])
        job        (utils/find-by-field jobs :extract-job/extract extract-id)
        job-status (:extract-job/status job)]
    [tr {:class
         (clsx
          "group transition-colors"
          {"cursor-pointer" extract-details-drawer?})

         ;; Used by the drawer outside-interaction handler to keep the drawer open
         ;; when switching extracts.
         :data-extract-row (when extract-details-drawer? "")

         :on-click
         (when extract-details-drawer?
           (fn [e]
             (.stopPropagation e)
             (on-open)))}
     [td {:class (clsx "pl-3 pr-2"
                       {"group-hover:bg-kleene-hover-background" extract-details-drawer?
                        "bg-kleene-selected-background" (and extract-details-drawer? active?)})}
      [:div {:on-click #(.stopPropagation %)}
       [fields/checkbox {:checked   selected
                         :class     (clsx "opacity-0 group-hover:opacity-100 transition-opacity"
                                          {"opacity-100" selected})
                         :on-change on-toggle}]]]
     [td {:class (clsx "max-w-[250px] overflow-hidden"
                       {"group-hover:bg-kleene-hover-background" extract-details-drawer?
                        "bg-kleene-selected-background" (and extract-details-drawer? active?)})}
      [:div {:title name :class "text-ellipsis overflow-hidden whitespace-nowrap"} name]
      (when description
        [typography/p {:class "text-sm break-all text-kleene-text-secondary"}
         description])]

     [td {:class (clsx "max-w-[200px] 2xl:max-w-[250px] overflow-hidden"
                       {"group-hover:bg-kleene-hover-background" extract-details-drawer?
                        "bg-kleene-selected-background" (and extract-details-drawer? active?)})}
      (let [schedule (app.utils/extract-schedule extract)]
        (if (= "No schedule" schedule)
          schedule
          [tooltip {:content schedule}
           [:div {:class "truncate cursor-help"} schedule]]))
      (when (= status "paused")
        [typography/p {:class "text-kleene-error-01"}
         "Paused"])]

     [td {:class (clsx {"group-hover:bg-kleene-hover-background" extract-details-drawer?
                        "bg-kleene-selected-background" (and extract-details-drawer? active?)})}
      [:div {:class "flex items-center gap-2"}
       (if message-queue-enabled
         [mq-extract-status-display extract-id]

         [job-status-display
          (if job-status
            job-status
            (keyword last-run-status))])
       (if message-queue-enabled
         [mq-extract-date-display extract-id timezone]

         (when last-run-at
           [date-tooltip {:date last-run-at :timezone timezone}]))]]

     [td {:class    (clsx {"group-hover:bg-kleene-hover-background" extract-details-drawer?
                           "bg-kleene-selected-background" (and extract-details-drawer? active?)})
          :on-click #(.stopPropagation %)}
      [:f> row-actions {:source            source
                        :extract           extract
                        :source-definition source-definition
                        :extract-details-drawer? extract-details-drawer?}]]]))

(defn empty-table-row []
  [tr
   [td {:col-span 5}
    [:div {:class "text-center py-4"}
     "No extracts found"]]])

(defn extracts-table-rows [{:keys [extracts source source-definition jobs selected-ids active-extract-id on-toggle on-open extract-details-drawer?]}]
  [:<>
   (for [{:extract/keys [extract-id] :as extract} extracts]
     ^{:key extract-id}
     [extracts-table-row {:extract                   extract
                          :source                    source
                          :jobs                      jobs
                          :source-definition         source-definition
                          :selected                  (some #(= % extract-id) selected-ids)
                          :extract-details-drawer?   extract-details-drawer?
                          :active?                   (= extract-id active-extract-id)
                          :on-toggle                 #(on-toggle extract-id)
                          :on-open                   #(on-open extract-id)}])])

(defn extracts-table-body [{:keys [source-id source source-details selected-ids active-extract-id on-toggle on-open extract-details-drawer?]}]
  (let [{:keys [extracts-loading? jobs]}
        @(re-posh/subscribe [:connections.subs/extracts-for-source source-id])

        {:extract-panel/keys [items]}
        @(re-posh/subscribe [:extract-panel/pull
                             '[* {:extract-panel/items [*]}]])

        items-ordered
        (sort-by :extract/order items)

        jobs-ordered
        (reverse (sort-by :extract-job/created-at jobs))]

    [tbody
     (cond
       (and (empty? items) (not extracts-loading?))
       [empty-table-row]

       :else
       [extracts-table-rows {:extracts                items-ordered
                             :source                  source
                             :jobs                    jobs-ordered
                             :source-definition       source-details
                             :selected-ids            selected-ids
                             :active-extract-id       active-extract-id
                             :on-toggle               on-toggle
                             :on-open                 on-open
                             :extract-details-drawer? extract-details-drawer?}])]))

(defn extracts-table [{:keys [source-id source source-details selected-ids active-extract-id on-toggle on-open]}]
  (let [{extracts :extract-panel/items}
        @(re-posh/subscribe [:extract-panel/pull
                             '[* {:extract-panel/items [*]}]])
        extract-details-drawer? @(re-posh/subscribe [:unleash/is-enabled "fe.extract-details-drawer"])
        ids (mapv :extract/extract-id extracts)
        all-selected? (cond
                        (and (not-empty ids)
                             (= (count selected-ids) (count ids)))
                        true

                        (not-empty selected-ids)
                        "indeterminate")]

    [table
     [extracts-table-head {:source-id               source-id
                           :source-details         source-details
                           :selected               all-selected?
                           :on-toggle              #(on-toggle (if all-selected? [] ids))
                           :on-clear-selection      #(on-toggle [])
                           :extract-details-drawer? extract-details-drawer?}]
     [extracts-table-body {:source-id               source-id
                           :source                  source
                           :source-details          source-details
                           :selected-ids            selected-ids
                           :on-toggle               on-toggle
                           :active-extract-id       active-extract-id
                           :on-open                 on-open
                           :extract-details-drawer? extract-details-drawer?}]]))

(defn fetch-more-extracts []
  (let [{:extract-panel/keys [pagination sort]}
        @(re-posh/subscribe [:extract-panel/pull])
        {:keys [source-id]} @(re-posh/subscribe [:app-route/prop :app-route/params])]
    (when (> (:total-pages pagination) (:page pagination))
      (dispatch [:connections.api/fetch-extracts {:source-id  source-id
                                                  :pagination (update pagination :page inc)
                                                  :sort       sort}]))))


(defn bulk-pause [ids source-id status on-success]
  (dispatch [:connections.events/bulk-pause-extracts
             {:extract-ids ids
              :source-id   source-id
              :status      status
              :on-success  on-success}]))

(defn bulk-extract-schedule [ids source-id on-success form-data]
  (let [{:keys [cron]} (form-component/model->fields form-data)]
    (dispatch [:connections.events/bulk-schedule-extracts
               {:extract-ids ids
                :source-id   source-id
                :data        {:schedule-spec {:cron cron}}
                :on-success  on-success}])))

(defn bulk-extract-unschedule [ids source-id on-success]
  (dispatch [:connections.events/bulk-unschedule-extracts
             {:extract-ids ids
              :source-id   source-id
              :on-success  on-success}]))


(defn schedule-dialog [{:keys [selected-ids source-id open on-success on-close]}]
  (let [timezone @(re-posh/subscribe [:app.subs/timezone])]
    [dialog-root {:open open
                  :on-open-change (fn [v]
                                    (when-not v (on-close)))}
     [dialog-content {:size :smallish}
      [dialog-title {:class "mb-4"} "Schedule " (count selected-ids) " extracts"]

      [form-component/form
       (->form-model
        {:id        :bulk-extract-scheduler
         :on-submit (partial bulk-extract-schedule selected-ids source-id on-success)
         :clean-up  #(dispatch [:form-model/delete [:form-model/id :bulk-extract-scheduler]])
         :validate  (partial form-component/validate-form ::c.schedule/schedule-form)
         :fields    [{:name :cron :value app.utils/empty-cron :process identity}]})
       (fn [fields errors on-submit]
         (let [any-error (->> errors vals (map :value) first)]
           [:form {:on-submit on-submit :key fields}
            [:div {:class "flex justify-between"}
             [:div {:class "flex-1"}
              [ui.typography/h3 "Current schedule (" timezone " time zone)"]
              [ui.typography/h4 {:class "text-kleene-primary-v2 mb-4"}
               (app.utils/format-schedule
                {:cron            (get-in fields [:cron :value])
                 :empty-status    "Not scheduled"
                 :required-fields [:minute]})]]]

            [:hr {:class "py-4"}]

            [c.schedule/cron-editor {:field (:cron fields)}]

            (when (some? any-error)
              [:div {:class "mt-4 mb-4"}
               [:span {:class "text-kleene-error-01"} any-error]])

            [dialog-actions
             [ui.button/button {:type    "submit"
                                :data-cy "extract-schedule-submit-btn"}
              "Submit schedule"]]]))]]]))

(defn delete-dialog [{:keys [open selected-ids source-id on-success on-close]}]
  [dialog-root {:open open
                :on-open-change (fn [v]
                                  (when-not v (on-close)))}
   [dialog-content {:size :smallish}
    [dialog-title {:class "mb-4"} "Delete " (count selected-ids) " extracts"]

    [:div {:class "mb-4"}
     [typography/p "Are you sure you want to delete the selected extracts?"]]

    [dialog-actions
     [ui.button/button {:data-cy  "extract-delete-confirm-btn"
                        :on-click #(dispatch [:connections.events/bulk-delete-extracts
                                              {:extract-ids selected-ids
                                               :source-id   source-id
                                               :on-success  on-success}])}
      "Delete"]]]])


(defn bulk-actions-menu [{:keys [selected-ids source-id on-toggle]}]
  (let [{extracts :extract-panel/items}
        @(re-posh/subscribe [:extract-panel/pull
                             '[* {:extract-panel/items [*]}]])
        [show-schedule set-show-schedule] (react/useState false)
        [show-delete set-show-delete] (react/useState false)

        all-paused? (->> extracts
                         (filter (fn [e] (some #(= (:extract/extract-id e) %) selected-ids)))
                         (every? #(= (:extract/status %) "paused")))
        action (fn [f & args]
                 (apply f (concat [selected-ids source-id] args)))]
    [:<>
     [dropdown-menu-root
      [dropdown-menu-trigger
       [button {:data-cy   "bulk-actions"
                :icon      ChevronDown
                :variant   :secondary
                :component :div}
        "Bulk actions"]]
      [dropdown-menu-content


       (when (empty? selected-ids)
         [:div {:class "bg-kleene-warning-05 text-kleene-warning-01 px-1 m-1 rounded-md"}
          [:div {:class "mt-1 p-1"}
           "Select one or more extracts"]])

       [dropdown-menu-item {:on-click #(action bulk-pause (if all-paused? "active" "paused") (partial on-toggle []))
                            :disabled (empty? selected-ids)}
        (if all-paused?
          "Unpause"
          "Pause")]
       [dropdown-menu-item
        {:on-click #(js/setTimeout
                     (fn []
                       ;; HACK: We defer opening the dialog to the next tick to allow the dropdown
                       ;; to fully close first. Without this, the dropdown and dialog overlays overlap,
                       ;; leaving the body with `pointer-events: none` and freezing the UI.
                       ;; This should be removed once the new TypeScript components handle overlay
                       ;; lifecycle correctly.
                       (set-show-schedule true))
                     0)
         :disabled (empty? selected-ids)}
        "Schedule..."]
       [dropdown-menu-item {:on-click #(action bulk-extract-unschedule (partial on-toggle []))
                            :disabled (empty? selected-ids)}
        "Unschedule"]
       [dropdown-menu-item
        {:class "text-kleene-text-error"
         :on-click #(js/setTimeout
                     (fn []
                       ;; HACK: Same as above — defer dialog open to avoid dropdown/dialog
                       ;; lifecycle overlap which can leave the UI in a non-interactable state.
                       ;; Remove once TypeScript components replace current implementation.
                       (set-show-delete true))
                     0)
         :disabled (empty? selected-ids)}
        "Delete"]]]

     [delete-dialog {:open         show-delete
                     :selected-ids selected-ids
                     :source-id    source-id
                     :on-success   (fn []
                                     (set-show-delete false)
                                     (on-toggle []))
                     :on-close     #(set-show-delete false)}]

     [schedule-dialog {:open         show-schedule
                       :selected-ids selected-ids
                       :source-id    source-id
                       :on-success   (fn []
                                       (set-show-schedule false)
                                       (on-toggle []))
                       :on-close     #(set-show-schedule false)}]]))

(defn- extracts-count-label [{:keys [loaded total selected]}]
  (when (pos? loaded)
    (let [total (when (and total (>= total loaded)) total)]
      (str (if (= 1 (or total loaded))
             "1 extract"
             (str "1–" loaded (when total (str " of " total))))
           (when (pos? selected) (str " · " selected " selected"))))))

(defn extracts-count [{:keys [selected-ids]}]
  (let [{:extract-panel/keys [items pagination]}
        @(re-posh/subscribe [:extract-panel/pull
                             '[:extract-panel/pagination
                               {:extract-panel/items [:extract/extract-id]}]])]
    (when-let [label (extracts-count-label
                      {:loaded   (count items)
                       :total    (:total-records pagination)
                       :selected (count selected-ids)})]
      [:div {:class "px-8 pb-2 text-kleene-text/50 text-xs"
             :data-cy "extracts-count"}
       label])))

(defn source-panel [{:keys [sources connectors]}]
  (let [[selected-ids set-selected-ids] (react/useState [])
        extract-details-drawer? @(re-posh/subscribe [:unleash/is-enabled "fe.extract-details-drawer"])
        on-toggle (fn [id]
                    (if (vector? id)
                      (set-selected-ids id)
                      (set-selected-ids (fn [old-ids]
                                          (sort (app.utils/toggle old-ids id))))))

        route @(re-posh/subscribe [:app-route/pull])
        {:app-route/keys [query]} route
        {{:keys [source-id] active-extract-id :extract-id} :app-route/params} route
        active-tab (or (:tab query) "details")

        open-extract-drawer (fn [id]
                              (dispatch [:app.events/navigate :extract-details
                                         {:source-id source-id :extract-id id}
                                         (assoc query :tab "details")]))

        close-extract-drawer (fn []
                               (dispatch [:app.events/navigate :source-details
                                          {:source-id source-id}
                                          (dissoc query :tab)]))

        set-drawer-tab (fn [tab]
                         (dispatch [:app.events/navigate-query {:tab tab}]))

        {:source/keys [source-type name description created-at created-by] :as active-source}
        (utils/find-by-field sources :source/source-id source-id)

        version            (or (:version active-source) (get-in active-source [:source/source :version]))
        source-details     (app.utils/find-connector-by-type-and-version connectors source-type version)
        documentation-link (:documentation-link source-details)

        connector-latest-version (:version (app.utils/find-connector-by-type-and-version connectors source-type nil))

        has-available-recipes? @(re-posh/subscribe [:recipes.subs/has-available-recipes-for-type source-type])
        loading?               @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-extracts])]


    (react/useEffect
     (fn []
       (set-selected-ids [])
       (fn []))
     #js [source-id])

    (react/useEffect
     (fn []
       (when (and extract-details-drawer? source-id active-extract-id)
         (fetch-extract-for-drawer active-extract-id source-id))

       (fn []))
     #js [source-id active-extract-id extract-details-drawer?])

    (react/useEffect
     (fn []
       (when (and source-id (:message-queue-enabled source-details))
         (dispatch [:connections.api/fetch-extract-statuses-for-source source-id]))

       (fn []))
     #js [source-id])

    [:div {:class "relative h-full overflow-hidden"}
     [:div {:class "relative z-10 flex flex-col h-full overflow-y-auto"}
      [:div {:class "border-b bg-kleene-background"}
       [:header {:class "px-5 py-4 top-0 flex justify-between items-center gap-4"}
        [:div
         [typography/h2 {:class "flex gap-2"}
          name
          [source-menu {:source active-source}]]
         (when description
           [typography/p {:class "text-kleene-text-secondary"}
            description])
         [typography/small {:class "text-kleene-text-secondary"}
          "Created on "
          [date-tooltip {:date created-at}]
          " by "
          (:searchable-user/username created-by)]
         (when-not (= version connector-latest-version)
           [:div {:class "flex items-center text-kleene-warning-02 pt-4"}
            [button {:size    :icon
                     :variant "standard"
                     :icon    AlertTriangle}]
            [typography/small
             [:<>
              [:span "This source is not using the latest connector version. Please "]
              [:a {:href   "http://support.kleene.ai/"
                   :target "_blank"
                   :class  "underline"}
               "raise a support ticket"]
              [:span " to upgrade."]]]])]
        [:div {:class "flex items-center gap-2 shrink-0"}
         [:f> bulk-actions-menu {:selected-ids selected-ids
                                 :source-id    source-id
                                 :on-toggle    on-toggle}]
         (when has-available-recipes?
           [button {:data-cy  "recipes-btn"
                    :icon     Utensils
                    :variant  :secondary
                    :on-click #(dispatch [:app.events/navigate-query {:recipes true}])}
            "Show recipes"])
         (when documentation-link
           [button {:data-cy "source-docs-btn"
                    :href    documentation-link
                    :target  "_blank"
                    :icon    BookOpen
                    :variant :secondary}
            "Docs"])
         [button {:data-cy  "add-extract-btn"
                  :icon     Plus
                  :variant  :secondary
                  :on-click #(show-create-extract-modal {:id   source-id
                                                         :type source-type})}
          "Add extract"]]]

       [extracts-count {:selected-ids selected-ids}]]

      [c.infinite-scroll/main
       {:component     extracts-table-wrapper
        :loading?      loading?
        :loader        [:div {:class "p-4 flex justify-center"}
                        [c.spinner/spinner-lucide]]
        :on-scroll-end fetch-more-extracts}
       [extracts-table {:source-id          source-id
                        :source            active-source
                        :source-details    source-details
                        :selected-ids      selected-ids
                        :active-extract-id active-extract-id
                        :on-toggle         on-toggle
                        :on-open           open-extract-drawer}]]]

     (when extract-details-drawer?
       [drawer-root {:open           (boolean active-extract-id)
                     :on-open-change (fn [open?] (when-not open? (close-extract-drawer)))
                     :modal          false}
        [drawer-content-resizable
         {:force-mount true
          :on-interact-outside
          (fn [event]
            (let [original (some-> event .-detail .-originalEvent)
                  target   (some-> original .-target)]
              (when (or (instance? js/FocusEvent original)
                        (and target (.closest target "[data-extract-row]")))
                (.preventDefault event))))}
         [:> Allotment {:class         "h-full"
                        :default-sizes (clj->js [50 50])}
          [:> Allotment.Pane {:min-size 0}
           [:div {:class "h-full"}]]

          [:> Allotment.Pane {:min-size 640
                              :max-size 1050
                              :snap     true
                              :visible  (boolean active-extract-id)}
           [:div {:class "h-full border-l bg-kleene-background pointer-events-auto"
                  :data-extract-details-ag-popup-parent ""}
            (when active-extract-id
              (let [extract @(re-posh/subscribe [:extract/pull [:extract/extract-id active-extract-id]])]
                (when extract
                  [extract-details-drawer
                   {:extract           extract
                    :source            active-source
                    :source-definition source-details
                    :active-tab        active-tab
                    :on-tab-change     set-drawer-tab
                    :on-close          close-extract-drawer
                    :on-run            #(dispatch [:connections.api/start-extract-by-id
                                                    {:extract-id active-extract-id
                                                     :name       (:extract/name extract)
                                                     :source-id  source-id
                                                     :source     source-details}])
                    :on-clone          #(dispatch [:connector/add
                                                    {:connector/action :clone-extract
                                                     :connector/type   (:source/source-type active-source)
                                                     :connector/id     source-id
                                                     :connector/meta   {:extract extract}}])
                    :on-delete         #(dispatch [:connections.events/bulk-delete-extracts
                                                    {:extract-ids [active-extract-id]
                                                     :source-id   source-id
                                                     :on-success  close-extract-drawer}])}])))]]]]])]))


(defn panel [{:keys [sources connectors active-id loading? switch-tab]}]
  (cond
    (not active-id)
    [:div {:class "flex h-full items-center justify-center"}
     [typography/h2 "Select a source"]]

    loading?
    [:div {:class "flex p-4 items-center justify-center w-full h-full overflow-hidden"}
     [c.spinner/spinner-lucide {:size 48}]]

    (empty? sources)
    [empty-message {:switch-tab switch-tab}]

    :else
    [:f> source-panel {:connectors connectors
                       :sources    sources
                       :loading?   loading?}]))

(defn on-init []
  (let [{{:keys [source-id]} :app-route/params}
        @(re-posh/subscribe [:app-route/pull])
        sources-sort-options (-> @(re-posh/subscribe [:app.subs/get-local-storage "sources-panel-sort"])
                                 (js->clj :keywordize-keys true))
        extract-sort-options (-> @(re-posh/subscribe [:app.subs/get-local-storage "extracts-panel-sort"])
                                 (js->clj :keywordize-keys true))]
    (dispatch [:sources-panel/add {:sources-panel/sort sources-sort-options}])
    (dispatch [:connections.api/fetch-user-connectors])
    (dispatch [:users.api/fetch-searchable-users])
    (dispatch [:connections.events/load-data])
    (dispatch [:recipes.api/fetch-recipes])
    (dispatch [:transformations-search/add {:transformations-search/type :transform}])
    (dispatch [:connections.api/fetch-extract-statuses {:status "fail"}])

    (when source-id
      (dispatch [:extract-panel/add {:extract-panel/sort extract-sort-options}])
      (dispatch [:connections.api/fetch-extracts {:source-id source-id}]))))

(defn on-update [props old-props]
  (let [source-id     (get-in props [:params :source-id])
        old-source-id (get-in old-props [:params :source-id])]

    (when (and source-id (not= source-id old-source-id))
      (let [extract-sort-options (-> @(re-posh/subscribe [:app.subs/get-local-storage "extracts-panel-sort"])
                                     (js->clj :keywordize-keys true))]
        (dispatch [:app.events/navigate-query {:tab nil}])
        (dispatch-sync [:extract-panel/delete])
        (dispatch-sync [:extract-panel/add {:extract-panel/sort extract-sort-options}])
        (dispatch [:connections.api/fetch-extracts {:source-id source-id
                                                    :replace   true}])))))

(defn sources-and-extracts [{:keys [switch-tab]}]
  (reagent/create-class
   {:display-name
    "sources-and-extracts"

    :constructor
    (fn [] (on-init))

    :component-did-update
    (fn [this [_ old-props]]
      (let [props (reagent/props this)]
        (on-update props old-props)))

    :component-will-unmount
    (fn []
      (dispatch-sync [:sources-panel/delete])
      (dispatch-sync [:extract-panel/delete]))

    :render
    (fn []
      (let [{:sources-panel/keys [sources order-by order-dir]}
            @(re-posh/subscribe [:sources-panel/pull '[* {:sources-panel/sources
                                                          [* {:source/updated-by [:searchable-user/username]
                                                              :source/created-by [:searchable-user/username]}]}]])
            {:keys [connectors connectors-loading? sources-loading?]}
            @(re-posh/subscribe [:connections.subs/sources-list-view])

            sources (if order-by
                      (sort-by :source/order sources)
                      (sort-by (juxt :source/source-type :source/name :source/created-at) sources))

            {:keys [source-id]} @(re-posh/subscribe [:app-route/prop :app-route/params])]
        [:<>
         [page-columns
          [:f> sidebar {:sources    sources
                        :active-id  source-id
                        :connectors connectors
                        :loading?   sources-loading?
                        :order-by   order-by
                        :order-dir  order-dir}]

          [panel {:sources     sources
                  :connectors  connectors
                  :active-id   source-id
                  :loading?    (or sources-loading? connectors-loading?)
                  :switch-tab  switch-tab}]

          [deploy-recipes-dialog]]]))}))
